import { proxy, useSnapshot, ref } from 'valtio'
import { intl } from 'lang/intl'
import { throttle } from 'lodash-es'

import { getWebinarConnectInfo } from '../global/blocs/useConnectInfo'
import { getRole } from '../global/blocs/roomInfo'
import { showNotice } from '../Layout/Notices/Notices'
import { logMessage } from '../Logging/log'

// ===== Local Storage =====
const savePreference = (kind: MediaDeviceInfo['kind'], value?: string) => {
  if (!value || value === '') return // Don't save "no device selected" as preference

  try {
    window.localStorage.setItem(`tt:${kind}`, JSON.stringify(value || 'none'))
  } catch (e) {
    logMessage('Could not save media device preference', {
      level: 'warning',
      extra: { kind, value, reason: e },
    })
  }
}

const getPreference = (kind: MediaDeviceInfo['kind']) => {
  try {
    const data = window.localStorage.getItem(`tt:${kind}`)
    if (data) return JSON.parse(data)
  } catch (e) {
    logMessage('Could not retrieve media device preference', {
      level: 'warning',
      extra: { kind, reason: e },
    })
  }
  return undefined
}

// ===== State Management =====

type DeviceList = {
  videoinput: InputDeviceInfo[]
  audioinput: InputDeviceInfo[]
  audiooutput: MediaDeviceInfo[]
}

type DevicesInfoState = {
  devices: DeviceList
  currentSelectedDevice: Record<MediaDeviceInfo['kind'], string | undefined>
  hasGrantedDevicesPermission: boolean
  mobileFacingMode: 'user' | 'environment'
}

const devicesState = proxy<DevicesInfoState>({
  hasGrantedDevicesPermission: false,
  devices: {
    audioinput: [],
    audiooutput: [],
    videoinput: [],
  },
  currentSelectedDevice: {
    audioinput: undefined,
    audiooutput: undefined,
    videoinput: undefined,
  },
  mobileFacingMode: 'user',
})

let isCheckingPermissions = false
const checkMediaPermissions = async () => {
  isCheckingPermissions = true
  const timeWhenRequestedPermission = Date.now()
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: { width: { ideal: 1920 } },
    })
    if (!devicesState.hasGrantedDevicesPermission) {
      logMessage('User got device permission', {
        level: 'debug',
        extra: {
          stream,
          settings: stream.getTracks().map((t) => t.getSettings()),
        },
      })
    }
    devicesState.hasGrantedDevicesPermission = true
    stream.getTracks().forEach((track) => track.stop())
    closeOpenNotice?.()
  } catch (error) {
    logMessage(
      devicesState.hasGrantedDevicesPermission
        ? 'User lost device permission'
        : 'Failed to get device permissions',
      { level: 'debug', extra: { reason: error } }
    )
    devicesState.hasGrantedDevicesPermission = false
    const canRequestPermission = Date.now() - timeWhenRequestedPermission > 200
    showNoticeForPermissionStatus(canRequestPermission)
  } finally {
    isCheckingPermissions = false
  }
}

const updateDevicesList = async () => {
  const deviceEnumeration = await navigator.mediaDevices.enumerateDevices()
  const devices = deviceEnumeration
    .filter((device) => !!device.deviceId)
    .map((device) => ref(device))

  devicesState.devices = devices.reduce<DeviceList>(
    (acc, device) => {
      // @ts-expect-error — we expect input device info to always extend InputDeviceInfo
      acc[device.kind].push(ref(device))
      return acc
    },
    { audioinput: [], audiooutput: [], videoinput: [] }
  )
}

const selectNewDevice = (kind: MediaDeviceInfo['kind'], newDevice?: string) => {
  savePreference(kind, newDevice)
  devicesState.currentSelectedDevice[kind] = newDevice
}

const changeSelectedDevices = (
  devicesIds: DevicesInfoState['currentSelectedDevice']
) => {
  const entries = Object.entries(devicesIds) as [
    MediaDeviceInfo['kind'],
    string | undefined,
  ][]
  entries.forEach(([kind, deviceId]) => {
    selectNewDevice(kind, deviceId)
  })
}

const updateSelectedDeviceForKind = (kind: MediaDeviceInfo['kind']) => {
  const currentSelectedDeviceId = devicesState.currentSelectedDevice[kind]

  if (currentSelectedDeviceId === undefined) {
    // first time updating devices

    const devices = devicesState.devices[kind]

    // Check if the stored device id can be found in among the current devices.
    const preferredDeviceId = getPreference(kind)
    const preferredDevice = devices.find(
      (d) => d.kind === kind && d.deviceId === preferredDeviceId
    )
    if (preferredDevice?.deviceId) {
      selectNewDevice(kind, preferredDevice.deviceId)
      return
    }

    // Try and find the default device
    const defaultDevice = devices.find(
      (d) => d.kind === kind && d.deviceId === 'default'
    )
    if (defaultDevice?.deviceId) {
      selectNewDevice(kind, defaultDevice.deviceId)
      return
    }

    // Any device will do
    const availableDevice = devices.find((d) => d.kind === kind)
    if (availableDevice?.deviceId) {
      selectNewDevice(kind, availableDevice.deviceId)
    }
  } else {
    if (currentSelectedDeviceId === '') return

    const devices = devicesState.devices[kind]

    const currentSelectedDevice = devices.find(
      (d) => d.kind === kind && d.deviceId === currentSelectedDeviceId
    )
    if (!currentSelectedDevice) {
      // if selected device can't be found, reset selection
      selectNewDevice(kind, undefined)
    }
  }
}

const synchronizeDevices = async () => {
  if (isCheckingPermissions) return
  await checkMediaPermissions()

  try {
    await updateDevicesList()

    updateSelectedDeviceForKind('audioinput')
    updateSelectedDeviceForKind('audiooutput')
    updateSelectedDeviceForKind('videoinput')
  } catch (e) {
    logMessage('An error occurred while initializing devices', {
      level: 'error',
      extra: { reason: e },
    })
  }
}

let closeOpenNotice: ReturnType<typeof showNotice> | null = null
const showNoticeForPermissionStatus = (canRequestPermission: boolean) => {
  closeOpenNotice?.()
  if (canRequestPermission) {
    closeOpenNotice = showNotice({
      message: intl.formatMessage({
        defaultMessage:
          'You need to grant permission to use your camera and microphone',
        id: '7dLHsF',
      }),
      cta: {
        label: intl.formatMessage({
          defaultMessage: 'Grant permission',
          id: 'mEhchJ',
        }),
        onClick: () => synchronizeDevices(),
        dismissOnClick: true,
      },
    })
  } else {
    closeOpenNotice = showNotice({
      message: intl.formatMessage({
        defaultMessage:
          "We couldn't get permissions to use your camera and microphone",
        id: 'ou4xY8',
      }),
      cta: {
        label: intl.formatMessage({ defaultMessage: 'Get Help', id: 'r0l0Kg' }),
        link: 'https://www.twentythree.com/help/webinars-test-and-host',
        dismissOnClick: true,
      },
      isClosable: true,
    })
  }
}

const swapVideoFacingMode = throttle(
  () => {
    devicesState.mobileFacingMode =
      devicesState.mobileFacingMode === 'user' ? 'environment' : 'user'
  },
  1500,
  { leading: true, trailing: false }
)

/// ////////////////////////////// ///
/// initialization and destruction ///
/// ////////////////////////////// ///

const subscriptions: (() => void)[] = []

const init = async () => {
  try {
    const { streaming_method } = await getWebinarConnectInfo()
    if (streaming_method === 'hardware') return // gear mode
    if (!['producer', 'presenter'].includes(getRole())) return

    window.addEventListener('focus', synchronizeDevices)
    subscriptions.push(() => {
      window.removeEventListener('focus', synchronizeDevices)
    })

    navigator.mediaDevices.addEventListener('devicechange', synchronizeDevices)
    subscriptions.push(() => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        synchronizeDevices
      )
    })

    await synchronizeDevices()
  } catch (e) {
    logMessage('Could not initialize devices', {
      level: 'error',
      extra: { reason: e },
    })
  }
}

const initializeDevicesAsAudience = async () => {
  await synchronizeDevices()

  navigator.mediaDevices.addEventListener('devicechange', updateDevicesList)
  subscriptions.push(() => {
    navigator.mediaDevices.removeEventListener(
      'devicechange',
      updateDevicesList
    )
  })

  return devicesState.hasGrantedDevicesPermission
}

const destroy = () => {
  while (subscriptions.length > 0) {
    subscriptions.shift()?.()
  }
}

/// ///////////////////////////////////////////////////////////////////////////

const useDevices = () => useSnapshot(devicesState).devices
const useSelectedDevices = () => useSnapshot(devicesState).currentSelectedDevice

const useSelectedAudioOutputDevice = () =>
  useSnapshot(devicesState).currentSelectedDevice.audiooutput
const useHasGrantedDevicesPermission = () =>
  useSnapshot(devicesState).hasGrantedDevicesPermission

const useSelectedAudioInputDevice = () =>
  useSnapshot(devicesState).currentSelectedDevice.audioinput

const useSelectedVideoInputDevice = () =>
  useSnapshot(devicesState).currentSelectedDevice.videoinput

const useMobileFacingMode = () => useSnapshot(devicesState).mobileFacingMode

const useCanChangeFacingMode = () => {
  const videoDevices = useDevices().videoinput

  const otherFacingMode =
    devicesState.mobileFacingMode === 'user' ? 'environment' : 'user'

  return videoDevices.some(
    (d) => !!d.getCapabilities().facingMode?.includes(otherFacingMode)
  )
}

export {
  devicesState,
  init,
  destroy,
  synchronizeDevices,
  initializeDevicesAsAudience,
  changeSelectedDevices,
  selectNewDevice,
  swapVideoFacingMode,
  useDevices,
  useSelectedDevices,
  useSelectedAudioInputDevice,
  useSelectedAudioOutputDevice,
  useSelectedVideoInputDevice,
  useHasGrantedDevicesPermission,
  useMobileFacingMode,
  useCanChangeFacingMode,
}
